import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import '../common/agent-element-realtime';
import { MicrophoneService } from '../services/microphone.service';

const AGENT_STRUCTURED_MESSAGE_EVENT = 'structured message';
const TRANSCRIPT_DELTA_EVENTS = new Set([
  'response.audio_transcript.delta',
  'response.output_audio_transcript.delta',
]);
const TRANSCRIPT_DONE_EVENTS = new Set([
  'response.audio_transcript.done',
  'response.output_audio_transcript.done',
]);

@Component({
  selector: 'app-hal-realtime',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="hal-realtime-host">
      <div #agentBox class="hal-realtime-host__agent"></div>
    </div>
  `,
  styleUrls: ['./hal.component.scss']
})
export class HalRealtimeComponent implements AfterViewInit, OnChanges, OnDestroy {
  private agent?: HTMLElement;
  private connectStarted = false;
  private caption = '';

  @ViewChild('agentBox', { static: true }) agentBoxRef!: ElementRef<HTMLDivElement>;

  @Input() cif: string | null = null;
  @Input() currentSessionId: string | null = null;
  @Input() currentUserName: string | null = null;

  /** What the agent is currently saying; cleared when the user starts speaking. */
  @Output() captionChange = new EventEmitter<string>();

  constructor(private microphoneService: MicrophoneService, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.mountAgent();
    void this.connectAgent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.agent) {
      return;
    }
    this.syncAgentAttributes();
  }

  ngOnDestroy(): void {
    this.agent?.removeEventListener(AGENT_STRUCTURED_MESSAGE_EVENT, this.handleAgentMessage);
    void this.disconnectAgent();
  }

  private mountAgent(): void {
    if (this.agent) {
      return;
    }

    this.agent = document.createElement('openai-realtime-agent') as HTMLElement;
    (this.agent as any).microphoneService = this.microphoneService;
    this.syncAgentAttributes();
    this.ngZone.runOutsideAngular(() => {
      this.agent!.addEventListener(AGENT_STRUCTURED_MESSAGE_EVENT, this.handleAgentMessage);
    });
    this.agentBoxRef.nativeElement.appendChild(this.agent);
  }

  private handleAgentMessage = (event: Event): void => {
    const data = (event as CustomEvent).detail as { type?: string; delta?: string; transcript?: string } | undefined;
    if (!data?.type) {
      return;
    }

    let next = this.caption;
    if (data.type === 'response.created') {
      next = '';
    } else if (TRANSCRIPT_DELTA_EVENTS.has(data.type)) {
      next = this.caption + (data.delta ?? '');
    } else if (TRANSCRIPT_DONE_EVENTS.has(data.type)) {
      next = data.transcript ?? this.caption;
    } else if (data.type === 'input_audio_buffer.speech_started') {
      next = '';
    } else {
      return;
    }

    // response.created resets the buffer silently so the previous caption stays until new speech arrives.
    const shouldEmit = data.type !== 'response.created' && next !== this.caption;
    this.caption = next;
    if (shouldEmit) {
      this.ngZone.run(() => this.captionChange.emit(next.trim()));
    }
  };

  private syncAgentAttributes(): void {
    if (!this.agent) {
      return;
    }

    this.agent.setAttribute('cif', this.cif ?? '');
    this.agent.setAttribute('currentSessionId', this.currentSessionId ?? '');
    this.agent.setAttribute('currentUserName', this.currentUserName ?? '');
    this.agent.setAttribute('width', '375');
    this.agent.setAttribute('height', '375');
  }

  async connectAgent(): Promise<void> {
    if (!this.agent || this.connectStarted) {
      return;
    }

    this.connectStarted = true;
    this.syncAgentAttributes();

    try {
      await (this.agent as any).connect();
      console.log('Agent connected');
    } catch (error) {
      this.connectStarted = false;
      console.error('Failed to connect:', error);
    }
  }

  async disconnectAgent(): Promise<void> {
    if (!this.agent) {
      return;
    }

    try {
      await (this.agent as any).disconnect();
      console.log('Agent disconnected');
    } catch (error) {
      console.error('Failed to disconnect:', error);
    } finally {
      this.connectStarted = false;
    }
  }
}
